import { motion } from 'motion/react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  BellIcon,
  CheckIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  UserGroupIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { useNotificationStore } from '../../stores/notificationStore';
import clsx from 'clsx';
import type { Notification, NotificationType } from '@affogato/shared';

const iconMap: Record<NotificationType, typeof BellIcon> = {
  portal_access: EyeIcon,
  file_download: ArrowDownTrayIcon,
  file_view: EyeIcon,
  portal_shared: UserGroupIcon,
  system: BellIcon,
  // Predictive alerts
  project_inactive: ClockIcon,
  project_due_soon: ExclamationTriangleIcon,
  project_overdue: ExclamationCircleIcon,
};

interface Props {
  onClose: () => void;
}

export function NotificationDropdown({ onClose: _onClose }: Props) {
  const { notifications, isLoading, markAsRead, markAllAsRead, deleteNotification, clearRead } =
    useNotificationStore();

  const handleMarkAsRead = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await markAsRead(id);
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteNotification(id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: 0.15 }}
      className={clsx(
        'absolute right-0 mt-2 w-96 max-h-[480px] overflow-hidden',
        'bg-white dark:bg-slate-800 rounded-xl shadow-xl',
        'border border-gray-200 dark:border-slate-700',
        'z-50'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
        <h3 className="font-semibold text-gray-900 dark:text-white">Notificaciones</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => markAllAsRead()}
            className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            Marcar todas como leídas
          </button>
        </div>
      </div>

      {/* Notifications list */}
      <div className="overflow-y-auto max-h-[360px]">
        {isLoading && notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-slate-400">
            Cargando...
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-slate-400">
            <BellIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No hay notificaciones</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.some((n) => n.readAt) && (
        <div className="p-3 border-t border-gray-200 dark:border-slate-700">
          <button
            onClick={() => clearRead()}
            className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Limpiar notificaciones leídas
          </button>
        </div>
      )}
    </motion.div>
  );
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
}: {
  notification: Notification;
  onMarkAsRead: (id: string, e: React.MouseEvent) => void;
  onDelete: (id: string, e: React.MouseEvent) => void;
}) {
  const Icon = iconMap[notification.type] || BellIcon;
  const isUnread = !notification.readAt;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className={clsx(
        'group flex items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer',
        'border-b border-gray-100 dark:border-slate-700/50 last:border-b-0',
        isUnread && 'bg-primary-50/50 dark:bg-primary-900/10'
      )}
    >
      <div
        className={clsx(
          'flex-shrink-0 p-2 rounded-lg',
          isUnread
            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
            : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
        )}
      >
        <Icon className="h-5 w-5" />
      </div>

      <div className="flex-1 min-w-0">
        <p
          className={clsx(
            'text-sm',
            isUnread
              ? 'font-medium text-gray-900 dark:text-white'
              : 'text-gray-700 dark:text-slate-300'
          )}
        >
          {notification.title}
        </p>
        <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
          {notification.message}
        </p>
        <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
          {formatDistanceToNow(new Date(notification.createdAt), {
            addSuffix: true,
            locale: es,
          })}
        </p>
      </div>

      <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {isUnread && (
          <button
            onClick={(e) => onMarkAsRead(notification.id, e)}
            className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            title="Marcar como leída"
          >
            <CheckIcon className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={(e) => onDelete(notification.id, e)}
          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
          title="Eliminar"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}
